import "./din276Tree.css";
import { Fragment } from "react";
import { Building2 } from "lucide-react";

import { expandUnit, type DinAttr } from "./din276Tree";

/**
 * Δ2 — column-aligned attribute datasheet. Direct grid children (fragments)
 * so the Typ/Einheit/Wertebereich columns align on a common edge; native
 * `title` tooltips keep the grid intact (no wrapper elements).
 *
 * Shared by the DIN 276 tree of the administration and the customer data model of the operator
 * obligations. The latter passes the customer's own attributes as `ownAttrs`; they follow the DIN
 * attributes, marked, so a reader sees at once which rows the norm gives and which the customer added.
 */
export default function Datasheet(props: { attrs: DinAttr[]; indent: number; ownAttrs?: DinAttr[]; ownLabel?: string }) {
  // A structure's fields follow it, one level deeper each — the datasheet stays one grid, and the
  // indentation carries the nesting.
  const flatten = (attrs: DinAttr[], own: boolean, depth: number): { attr: DinAttr; own: boolean; depth: number }[] =>
    attrs.flatMap((attr) => [{ attr, own, depth }, ...flatten(attr.children ?? [], own, depth + 1)]);
  const rows = [...flatten(props.attrs, false, 0), ...flatten(props.ownAttrs ?? [], true, 0)];

  return (
    <div className="din276tree-sheet" style={{ marginLeft: props.indent }}>
      <div className="din276tree-sheet-h">Attribut</div>
      <div className="din276tree-sheet-h">Typ</div>
      <div className="din276tree-sheet-h">Einheit</div>
      <div className="din276tree-sheet-h">Wertebereich</div>
      {rows.map(({ attr: a, own, depth }, i) => {
        const full = a.enum.join(" · ");
        const short = full.length > 44 ? full.slice(0, 43).trimEnd() + "…" : full;
        return (
          <Fragment key={i}>
            <div className="din276tree-sheet-nm" style={depth > 0 ? { paddingLeft: depth * 14 } : undefined}>
              {depth > 0 && <span className="din276tree-mute">↳ </span>}
              {a.name}
              {a.typeUnclear && <span className="din276tree-mute" title="Typ unklar"> ⚠</span>}
              {own && depth === 0 && (
                // Styled like the norm chip of a class: a reader compares where a row comes from —
                // DIN 276, a norm, or the customer — at a glance.
                <span
                  className="din276tree-norm norm-tenant din276tree-own"
                  title="Vom Kunden ergänzt — gilt nur für diesen Mandanten"
                >
                  <Building2 size={11} aria-hidden />
                  {props.ownLabel ?? "eigen"}
                </span>
              )}
            </div>
            <div className="din276tree-sheet-c">{a.type}</div>
            <div
              className={`din276tree-sheet-c${a.measured ? " measured" : ""}`}
              title={a.unit ? `${a.unit} — ${expandUnit(a.unit)}${a.measured ? " · gemessen / konformitätsrelevant" : ""}` : undefined}
            >
              {a.unit || ""}
            </div>
            <div className="din276tree-sheet-c din276tree-sheet-range" title={a.enum.length > 0 ? full : undefined}>
              {short}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
